<template>
  <div>
    <b-card>
      <b-overlay
        :show="isLoading"
        rounded="lg"
        spinner-variant="primary"
      >
        <!-- Header -->
        <div class="d-flex justify-content-between align-items-center mb-4">
          <div class="d-flex align-items-center">
            <b-avatar
              size="64"
              variant="light-primary"
              class="mr-3"
            >
              {{ getInitials(pickupeur.username) }}
            </b-avatar>
            <div>
              <h3 class="mb-0">
                {{ pickupeur.username || 'Ramasseur' }}
              </h3>
              <p class="text-muted mb-0">
                {{ pickupeur.email }}
              </p>
              <b-badge
                :variant="pickupeur.is_active ? 'light-success' : 'light-danger'"
                pill
              >
                {{ pickupeur.is_active ? 'Actif' : 'Inactif' }}
              </b-badge>
            </div>
          </div>
          <div>
            <b-button
              variant="outline-secondary"
              class="mr-2"
              :to="{ name: 'pickupeur-list' }"
            >
              <feather-icon
                icon="ArrowLeftIcon"
                class="mr-1"
              />
              Retour
            </b-button>
            <b-button
              variant="primary"
              :to="{ name: 'pickupeur-edit', params: { id: pickupeurId } }"
            >
              <feather-icon
                icon="EditIcon"
                class="mr-1"
              />
              Modifier
            </b-button>
          </div>
        </div>

        <b-row>
          <!-- Personal Information -->
          <b-col
            lg="6"
            md="12"
            class="mb-4"
          >
            <b-card
              title="Informations personnelles"
              class="h-100"
            >
              <b-list-group flush>
                <b-list-group-item class="d-flex justify-content-between align-items-center">
                  <div>
                    <feather-icon
                      icon="UserIcon"
                      size="16"
                      class="mr-2 text-muted"
                    />
                    Nom
                  </div>
                  <strong>{{ pickupeur.username || 'N/A' }}</strong>
                </b-list-group-item>

                <b-list-group-item class="d-flex justify-content-between align-items-center">
                  <div>
                    <feather-icon
                      icon="MailIcon"
                      size="16"
                      class="mr-2 text-muted"
                    />
                    Email
                  </div>
                  <strong>{{ pickupeur.email || 'N/A' }}</strong>
                </b-list-group-item>

                <b-list-group-item class="d-flex justify-content-between align-items-center">
                  <div>
                    <feather-icon
                      icon="PhoneIcon"
                      size="16"
                      class="mr-2 text-muted"
                    />
                    Téléphone
                  </div>
                  <strong>{{ pickupeur.phone || 'N/A' }}</strong>
                </b-list-group-item>

                <b-list-group-item
                  v-if="pickupeur.phone2"
                  class="d-flex justify-content-between align-items-center"
                >
                  <div>
                    <feather-icon
                      icon="PhoneIcon"
                      size="16"
                      class="mr-2 text-muted"
                    />
                    Téléphone 2
                  </div>
                  <strong>{{ pickupeur.phone2 }}</strong>
                </b-list-group-item>

                <b-list-group-item class="d-flex justify-content-between align-items-center">
                  <div>
                    <feather-icon
                      icon="MapPinIcon"
                      size="16"
                      class="mr-2 text-muted"
                    />
                    Adresse
                  </div>
                  <strong>{{ pickupeur.address || 'N/A' }}</strong>
                </b-list-group-item>
              </b-list-group>
            </b-card>
          </b-col>

          <!-- Work Information -->
          <b-col
            lg="6"
            md="12"
            class="mb-4"
          >
            <b-card
              title="Informations de travail"
              class="h-100"
            >
              <b-list-group flush>
                <b-list-group-item class="d-flex justify-content-between align-items-center">
                  <div>
                    <feather-icon
                      icon="HomeIcon"
                      size="16"
                      class="mr-2 text-muted"
                    />
                    Dépôt
                  </div>
                  <b-badge
                    v-if="pickupeur.repository_name"
                    variant="light-info"
                    pill
                  >
                    {{ pickupeur.repository_name }}
                  </b-badge>
                  <span
                    v-else
                    class="text-muted"
                  >Non assigné</span>
                </b-list-group-item>

                <b-list-group-item class="d-flex justify-content-between align-items-center">
                  <div>
                    <feather-icon
                      icon="TruckIcon"
                      size="16"
                      class="mr-2 text-muted"
                    />
                    Véhicule
                  </div>
                  <b-badge
                    v-if="pickupeur.car_details"
                    variant="light-secondary"
                    pill
                  >
                    {{ pickupeur.car_details.brand }} {{ pickupeur.car_details.model }} - {{ pickupeur.car_details.serial_number }}
                  </b-badge>
                  <span
                    v-else
                    class="text-muted"
                  >Non assigné</span>
                </b-list-group-item>

                <b-list-group-item class="d-flex justify-content-between align-items-center">
                  <div>
                    <feather-icon
                      icon="CalendarIcon"
                      size="16"
                      class="mr-2 text-muted"
                    />
                    Date de création
                  </div>
                  <strong>{{ formatDate(pickupeur.created_at) }}</strong>
                </b-list-group-item>

                <b-list-group-item class="d-flex justify-content-between align-items-center">
                  <div>
                    <feather-icon
                      icon="CheckCircleIcon"
                      size="16"
                      class="mr-2 text-muted"
                    />
                    Statut
                  </div>
                  <b-badge
                    :variant="pickupeur.is_active ? 'light-success' : 'light-danger'"
                    pill
                  >
                    {{ pickupeur.is_active ? 'Actif' : 'Inactif' }}
                  </b-badge>
                </b-list-group-item>
              </b-list-group>
            </b-card>
          </b-col>
        </b-row>

        <!-- Actions -->
        <b-card title="Actions">
          <b-row>
            <b-col
              md="4"
              class="mb-3"
            >
              <b-button
                v-if="isAdmin"
                variant="outline-primary"
                block
                @click="openPasswordModal"
              >
                <feather-icon
                  icon="LockIcon"
                  class="mr-1"
                />
                Changer mot de passe
              </b-button>
            </b-col>
            <b-col
              md="4"
              class="mb-3"
            >
              <b-button
                :variant="pickupeur.is_active ? 'outline-danger' : 'outline-success'"
                block
                @click="toggleActivation"
              >
                <feather-icon
                  :icon="pickupeur.is_active ? 'UserXIcon' : 'UserCheckIcon'"
                  class="mr-1"
                />
                {{ pickupeur.is_active ? 'Désactiver' : 'Activer' }}
              </b-button>
            </b-col>
          </b-row>
        </b-card>
      </b-overlay>
    </b-card>

    <!-- Modal for Changing Pickupeur Password -->
    <b-modal
      id="pickupeur-password-modal"
      v-model="showPasswordModal"
      centered
      hide-footer
      size="lg"
      title-tag="div"
      @hidden="resetPasswordForm"
    >
      <template #modal-header="{ close }">
        <div class="w-100 d-flex justify-content-between align-items-center">
          <div class="d-flex align-items-center">
            <div class="avatar bg-light-primary p-2 mr-2">
              <feather-icon
                icon="LockIcon"
                size="20"
                class="text-primary"
              />
            </div>
            <div>
              <h5 class="mb-0">
                Changer le mot de passe
              </h5>
              <small
                v-if="pickupeur"
                class="text-muted"
              >
                {{ pickupeur.username }} • {{ pickupeur.email || 'Pas d\'email' }}
              </small>
            </div>
          </div>
          <b-button
            variant="flat-secondary"
            size="sm"
            class="btn-icon"
            @click="close"
          >
            <feather-icon icon="XIcon" />
          </b-button>
        </div>
      </template>

      <div v-if="pickupeur">
        <!-- Password Audit Log Banner -->
        <b-alert
          v-if="pickupeur.last_password_change"
          show
          variant="light-primary"
          class="mb-3 border-left-primary shadow-sm"
        >
          <div class="d-flex align-items-center">
            <feather-icon
              icon="ClockIcon"
              size="20"
              class="mr-2 text-primary"
            />
            <div>
              <strong class="d-block text-dark font-weight-bold">
                Dernière modification du mot de passe
              </strong>
              <span class="small text-muted">
                Modifié le {{ formatDateTime(pickupeur.last_password_change.created_at) }} par <strong>{{ pickupeur.last_password_change.changed_by_display }}</strong>
              </span>
            </div>
          </div>
        </b-alert>

        <!-- Collapsible Audit History -->
        <div
          v-if="pickupeur.password_change_history && pickupeur.password_change_history.length > 0"
          class="mb-3"
        >
          <b-button
            v-b-toggle.password-history-collapse
            variant="flat-primary"
            size="sm"
            class="p-0 font-weight-bold"
          >
            <feather-icon
              icon="HistoryIcon"
              size="14"
              class="mr-25"
            />
            Historique des modifications ({{ pickupeur.password_change_history.length }})
          </b-button>

          <b-collapse
            id="password-history-collapse"
            class="mt-2"
          >
            <b-list-group
              flush
              class="border rounded"
            >
              <b-list-group-item
                v-for="log in pickupeur.password_change_history"
                :key="log.id"
                class="py-50 px-1 d-flex justify-content-between align-items-center small"
              >
                <span>
                  <feather-icon
                    icon="UserCheckIcon"
                    size="12"
                    class="mr-50 text-primary"
                  />
                  <strong>{{ log.changed_by_display }}</strong>
                </span>
                <span class="text-muted">{{ formatDateTime(log.created_at) }}</span>
              </b-list-group-item>
            </b-list-group>
          </b-collapse>
        </div>

        <b-form @submit.prevent="savePasswordChange">
          <!-- New Password -->
          <b-form-group
            label-class="form-label-premium"
            label="Nouveau mot de passe *"
            label-for="modal-password"
            class="mb-3"
          >
            <b-input-group class="input-group-merge">
              <b-form-input
                id="modal-password"
                v-model="passwordForm.password"
                class="premium-input"
                :type="showPassword ? 'text' : 'password'"
                placeholder="Entrez le nouveau mot de passe"
                required
              />
              <b-input-group-append is-text>
                <feather-icon
                  :icon="showPassword ? 'EyeOffIcon' : 'EyeIcon'"
                  class="cursor-pointer"
                  @click="showPassword = !showPassword"
                />
              </b-input-group-append>
            </b-input-group>

            <!-- Password Strength Meter -->
            <div
              v-if="passwordForm.password"
              class="mt-2"
            >
              <div class="d-flex justify-content-between align-items-center mb-50">
                <small class="text-muted">Force du mot de passe:</small>
                <small
                  :class="'text-' + passwordStrengthClass"
                  class="font-weight-bold"
                >
                  {{ passwordStrengthText }}
                </small>
              </div>
              <b-progress
                :value="passwordStrength"
                :variant="passwordStrengthClass"
                height="6px"
              />
            </div>
          </b-form-group>

          <!-- Confirm Password -->
          <b-form-group
            label-class="form-label-premium"
            label="Confirmer le mot de passe *"
            label-for="modal-confirm-password"
            class="mb-3"
          >
            <b-input-group class="input-group-merge">
              <b-form-input
                id="modal-confirm-password"
                v-model="passwordForm.confirm_password"
                class="premium-input"
                :type="showConfirmPassword ? 'text' : 'password'"
                placeholder="Confirmez le mot de passe"
                required
              />
              <b-input-group-append is-text>
                <feather-icon
                  :icon="showConfirmPassword ? 'EyeOffIcon' : 'EyeIcon'"
                  class="cursor-pointer"
                  @click="showConfirmPassword = !showConfirmPassword"
                />
              </b-input-group-append>
            </b-input-group>

            <small
              v-if="passwordForm.confirm_password && !passwordsMatch"
              class="text-danger mt-50 d-block"
            >
              Les mots de passe ne correspondent pas.
            </small>
          </b-form-group>

          <!-- Generate Password Button -->
          <!-- Generate Password Button -->
          <div class="mb-4">
            <PasswordGenerator
              :user="pickupeur"
              user-type="pickupeur"
              button-text="Générer un mot de passe sécurisé"
              @generated="onPasswordGenerated"
            />
          </div>

          <!-- Action Footer -->
          <div class="d-flex justify-content-end">
            <b-button
              variant="outline-secondary"
              class="mr-2"
              @click="showPasswordModal = false"
            >
              Annuler
            </b-button>
            <b-button
              variant="primary"
              :disabled="isPasswordSubmitting || !isPasswordFormValid"
              type="submit"
            >
              <b-spinner
                v-if="isPasswordSubmitting"
                small
                class="mr-50"
              />
              <feather-icon
                v-else
                icon="SaveIcon"
                size="16"
                class="mr-50"
              />
              Enregistrer le mot de passe
            </b-button>
          </div>
        </b-form>
      </div>
    </b-modal>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BButton,
  BOverlay,
  BBadge,
  BAvatar,
  BListGroup,
  BListGroupItem,
  BModal,
  BAlert,
  BCollapse,
  BForm,
  BFormGroup,
  BFormInput,
  BInputGroup,
  BInputGroupAppend,
  BProgress,
  BSpinner,
  VBToggle,
} from 'bootstrap-vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import PasswordGenerator from '@core/components/password/PasswordGenerator.vue'
import axios from 'axios'
import storeAuth from '@/store/store'

export default {
  directives: {
    'b-toggle': VBToggle,
  },
  components: {
    PasswordGenerator,
    BCard,
    BRow,
    BCol,
    BButton,
    BOverlay,
    BBadge,
    BAvatar,
    BListGroup,
    BListGroupItem,
    BModal,
    BAlert,
    BCollapse,
    BForm,
    BFormGroup,
    BFormInput,
    BInputGroup,
    BInputGroupAppend,
    BProgress,
    BSpinner,
  },

  data() {
    return {
      isLoading: false,
      pickupeur: {},
      user: {},
      // Password modal data
      showPasswordModal: false,
      passwordForm: {
        password: '',
        confirm_password: '',
      },
      showPassword: false,
      showConfirmPassword: false,
      isPasswordSubmitting: false,
    }
  },

  computed: {
    pickupeurId() {
      return this.$route.params.id
    },
    isAdmin() {
      return this.user && this.user.role === 'admin'
    },
    // Password strength computation
    passwordStrength() {
      let strength = 0
      const { password } = this.passwordForm

      if (!password) return 0

      if (password.length >= 8) strength += 25
      if (/[A-Z]/.test(password)) strength += 25
      if (/[0-9]/.test(password)) strength += 25
      if (/[^A-Za-z0-9]/.test(password)) strength += 25

      return strength
    },

    passwordStrengthClass() {
      if (this.passwordStrength <= 25) return 'danger'
      if (this.passwordStrength <= 50) return 'warning'
      if (this.passwordStrength <= 75) return 'info'
      return 'success'
    },

    passwordStrengthText() {
      if (this.passwordStrength <= 25) return 'Faible'
      if (this.passwordStrength <= 50) return 'Moyen'
      if (this.passwordStrength <= 75) return 'Bon'
      return 'Fort'
    },

    passwordsMatch() {
      return (
        this.passwordForm.password
        && this.passwordForm.confirm_password
        && this.passwordForm.password === this.passwordForm.confirm_password
      )
    },

    isPasswordFormValid() {
      return (
        this.passwordForm.password
        && this.passwordForm.confirm_password
        && this.passwordsMatch
        && this.passwordForm.password.length >= 8
      )
    },
  },

  created() {
    this.user = storeAuth.state.user
    this.fetchPickupeur()
  },

  methods: {
    getInitials(name) {
      if (!name) return '?'
      return name
        .split(' ')
        .map(n => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2)
    },

    formatDate(date) {
      if (!date) return 'N/A'
      return new Date(date).toLocaleDateString('fr-FR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    },

    async fetchPickupeur() {
      this.isLoading = true
      try {
        const response = await axios.get(`/api/auth/users/pickupeurs/${this.pickupeurId}/`)
        const { data } = response

        this.pickupeur = {
          ...data,
          repository_name: data.repository_name || data.repository?.name || null,
          car_info: data.car ? `${data.car.brand || ''} - ${data.car.serial_number || ''}`.trim() : null,
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching pickupeur:', error)
        this.showToast('danger', 'Erreur lors du chargement du ramasseur')
        this.$router.push({ name: 'pickupeur-list' })
      } finally {
        this.isLoading = false
      }
    },

    async toggleActivation() {
      try {
        await axios.put(`/api/auth/users/pickupeurs/toggle-activation/${this.pickupeurId}/`)
        this.pickupeur.is_active = !this.pickupeur.is_active
        this.showToast('success', `Ramasseur ${this.pickupeur.is_active ? 'activé' : 'désactivé'} avec succès`)
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error toggling activation:', error)
        this.showToast('danger', 'Erreur lors du changement de statut')
      }
    },

    // Password modal methods
    openPasswordModal() {
      this.showPasswordModal = true
    },

    resetPasswordForm() {
      this.passwordForm = {
        password: '',
        confirm_password: '',
      }
      this.showPassword = false
      this.showConfirmPassword = false
      this.isPasswordSubmitting = false
    },

    onPasswordGenerated(generatedPassword) {
      this.passwordForm.password = generatedPassword
      this.passwordForm.confirm_password = generatedPassword
      this.showPassword = true
      this.showConfirmPassword = true
    },

    formatDateTime(dateStr) {
      if (!dateStr) return 'N/A'
      try {
        const date = new Date(dateStr)
        if (Number.isNaN(date.getTime())) return dateStr
        const day = String(date.getDate()).padStart(2, '0')
        const month = String(date.getMonth() + 1).padStart(2, '0')
        const year = date.getFullYear()
        const hours = String(date.getHours()).padStart(2, '0')
        const minutes = String(date.getMinutes()).padStart(2, '0')
        return `${day}/${month}/${year} à ${hours}:${minutes}`
      } catch (e) {
        return dateStr
      }
    },

    async savePasswordChange() {
      if (!this.isPasswordFormValid) return

      this.isPasswordSubmitting = true
      try {
        await axios.post(
          `/api/auth/users/pickupeurs/change-password/${this.pickupeur.id}/`,
          this.passwordForm,
        )

        const userDisplayName = this.user
          ? `${this.user.username} (${this.user.role || 'Utilisateur'})`
          : 'Utilisateur connecté'

        const newAuditLog = {
          id: Date.now(),
          changed_by_name: this.user ? this.user.username : 'Moi',
          changed_by_display: userDisplayName,
          created_at: new Date().toISOString(),
        }

        this.$set(this.pickupeur, 'last_password_change', newAuditLog)
        if (!this.pickupeur.password_change_history) {
          this.$set(this.pickupeur, 'password_change_history', [])
        }
        this.pickupeur.password_change_history.unshift(newAuditLog)

        this.showToast('success', 'Mot de passe modifié avec succès')
        this.showPasswordModal = false
        this.resetPasswordForm()
      } catch (error) {
        const errorMsg = error.response?.data?.password?.[0]
          || error.response?.data?.confirm_password?.[0]
          || error.response?.data?.detail
          || error.response?.data?.error
          || 'Erreur lors du changement de mot de passe'

        this.showToast('danger', errorMsg)
      } finally {
        this.isPasswordSubmitting = false
      }
    },

    showToast(variant, text) {
      let title
      let icon

      if (variant === 'success') {
        title = 'Succès'
        icon = 'CheckCircleIcon'
      } else if (variant === 'warning') {
        title = 'Attention'
        icon = 'AlertTriangleIcon'
      } else {
        title = 'Erreur'
        icon = 'XCircleIcon'
      }

      this.$toast({
        component: ToastificationContent,
        props: {
          title,
          icon,
          text,
          variant,
        },
      })
    },
  },
}
</script>
